import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, Upload, FileText, X, GraduationCap } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function ExamCreate() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [book, setBook] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error(t("Inserisci il nome dell'esame")); return; }
    if (!text.trim() && files.length === 0) { toast.error(t("Aggiungi appunti o foto")); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("book_ref", book);
    fd.append("notes_text", text);
    fd.append("lang", lang);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/exams", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Esame in preparazione! L'AI sta dividendo gli appunti 🚀"));
      navigate(`/exam/${data.exam_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-bg min-h-screen pb-20">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-5">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="examcreate-back-btn">
          <ChevronLeft size={18} /> {t("Home")}
        </button>
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap style={{ color: "var(--success)" }} />
          <h1 className="font-heading font-black text-3xl tracking-tight">{t("Nuovo Esame")}</h1>
        </div>
        <p className="text-white/60 mb-6 text-sm">{t("Carica tutti gli appunti e le fonti dell'esame (anche foto scritte a mano). L'AI li divide automaticamente in argomenti e crea 50+ domande e flashcard per ognuno.")}</p>

        <div className="glass rounded-3xl p-5 space-y-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Nome esame (es. Aerodinamica)")}
            className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none placeholder-white/40" data-testid="examcreate-name-input" />
          <input value={book} onChange={(e) => setBook(e.target.value)} placeholder={t("Libro / fonte (es. Anderson - Aerodynamics)")}
            className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none placeholder-white/40" data-testid="examcreate-book-input" />
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={t("Incolla qui gli appunti dell'intero esame...")}
            className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none placeholder-white/40 resize-none" data-testid="examcreate-notes-input" />
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 rounded-2xl py-5 cursor-pointer bouncy hover:border-white/40" data-testid="examcreate-file-label">
            <Upload size={20} style={{ color: "var(--cyan)" }} />
            <span className="text-sm text-white/70 text-center">{t("Carica foto appunti (anche a mano), PDF o testo")}</span>
            <input type="file" multiple accept=".pdf,.txt,.md,image/*" onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])} className="hidden" data-testid="examcreate-file-input" />
          </label>
          {files.length > 0 && (
            <div className="space-y-2" data-testid="examcreate-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-black/30 rounded-xl px-3 py-2 text-sm">
                  <FileText size={16} className="text-white/60" /><span className="flex-1 truncate">{f.name}</span>
                  <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-white/50 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
          <button onClick={submit} disabled={busy} className="w-full py-4 rounded-2xl btn-neon font-heading font-bold bouncy disabled:opacity-60" data-testid="examcreate-submit-btn">
            {busy ? t("Invio...") : t("🚀 Crea esame con l'AI")}
          </button>
        </div>
      </div>
    </div>
  );
}
